import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useFileUpload } from "@/hooks/useFileUpload";
import { ScoutBookmark } from "@/hooks/useFieldScout";

export const useVideoAnalysis = () => {
  const [uploading, setUploading] = useState(false);
  const { orgId } = useAuth();
  const { uploadFile } = useFileUpload(orgId || '');

  // Upload video (file or blob) to storage
  const uploadVideo = async (file: File | Blob, jobId: string, orgId: string, fileName?: string) => {
    try {
      setUploading(true);
      
      // Convert Blob to File if needed
      const fileToUpload = file instanceof File 
        ? file 
        : new File([file], fileName || `scout-${Date.now()}.webm`, { type: 'video/webm' });
      
      const result = await uploadFile(fileToUpload, {
        bucket: 'video-reports',
        folder: orgId,
      });

      if (!result) throw new Error('Upload failed');

      toast.success('Video uploaded! Extract frames to analyze.');
      
      return result;
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload video');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // Extract frames at bookmarked timestamps
  const extractBookmarkedFrames = async (
    videoUrl: string,
    bookmarks: ScoutBookmark[]
  ): Promise<{ frames: string[]; metadata: any[] }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.crossOrigin = 'anonymous';

      const frames: string[] = [];
      const metadata: any[] = [];
      let currentIndex = 0;

      video.onloadedmetadata = () => {
        const extractNextFrame = () => {
          if (currentIndex >= bookmarks.length) {
            resolve({ frames, metadata });
            return;
          }

          const bookmark = bookmarks[currentIndex];
          video.currentTime = bookmark.timestamp;

          video.onseeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            
            if (ctx) {
              ctx.drawImage(video, 0, 0);
              const frameData = canvas.toDataURL('image/jpeg', 0.9);
              frames.push(frameData);
              metadata.push({
                timestamp: bookmark.timestamp,
                trade: bookmark.trade,
                damageType: bookmark.damageType,
                gps: bookmark.gps
              });
            }

            currentIndex++;
            extractNextFrame();
          };
        };

        extractNextFrame();
      };

      video.onerror = () => reject(new Error('Failed to load video'));
    });
  };

  return {
    uploading,
    uploadVideo,
    extractBookmarkedFrames,
  };
};
